import { inject, Injectable, signal } from '@angular/core';
import { DocumentType } from '@softapps/wws/pos-core';
import { catchError, finalize, firstValueFrom, throwError } from 'rxjs';
import { DocumentRepository } from '../repositories/document.repository';
import { Scan } from '@softline/application';

export const DISTRIBUTION_TYPES = [
  {
    id: 1,
    name: 'Selbstabholung',
    value: 'Selbstabholung',
    label: 'Selbstabholung',
    icon: 'fa-regular fa-hand text-3xl text-primary',
  },
  {
    id: 2,
    name: 'Lieferung',
    value: 'Lieferung',
    label: 'Lieferung',
    icon: 'fa-regular fa-truck text-3xl text-primary',
  },
];

export interface FindDocumentQuery {
  documentType: DocumentType;
  documentNumber: string;
  salesOrganisationId: number;
}

@Injectable()
export class DocumentService {
  readonly #repository = inject(DocumentRepository);
  readonly #loading = signal(false);

  readonly documents = signal<any[]>([]);
  readonly positions = signal<any[]>([]);
  readonly loading = this.#loading.asReadonly();

  async find(query: FindDocumentQuery): Promise<any[]> {
    this.#loading.set(true);

    const documents = await firstValueFrom(
      this.#repository
        .find(query.documentType, query.documentNumber ?? '', query.salesOrganisationId)
        .pipe(
          finalize(() => this.#loading.set(false)),
          catchError((e) => {
            this.#loading.set(false);
            return throwError(() => e);
          })
        )
    );

    this.documents.set(documents);
    return documents;
  }

  async scan(scan: Scan, salesOrganisationId: number): Promise<any | null> {
    this.#loading.set(true);
    try {
      const document = await this.#repository.scan(scan.labelType ?? 'code39', scan.data);
      const result = await this.find({
        salesOrganisationId,
        documentNumber: document.belegnummer + '',
        documentType: scan.data.startsWith('KA')
          ? 'receipt'
          : scan.data.startsWith('LF')
            ? 'delivery_note'
            : scan.data.startsWith('AR')
              ? 'invoice'
              : 'cash_invoice',
      });
      this.#loading.set(false);
      return result.length > 0 ? result[0] : null;
    } catch {
      this.#loading.set(false);
      return null;
    }
  }

  async getPositions(documentType: DocumentType, documentId: number): Promise<any[]> {
    this.#loading.set(true);

    const positions = await firstValueFrom(
      this.#repository.getPositions(documentType, documentId).pipe(
        finalize(() => this.#loading.set(false)),
        catchError((e) => {
          this.#loading.set(false);
          return throwError(() => e);
        })
      )
    );
    this.positions.set(positions);
    return positions;
  }

  clear() {
    this.#loading.set(false);
    this.documents.set([]);
  }

  async print(params: {
    cartId: number;
    documentType: DocumentType;
    copies?: number;
  }): Promise<void> {
    await this.#repository.print(params.cartId, params.documentType, params?.copies ?? 1);
  }

  async reprintLastReceipt(copies = 1): Promise<void> {
    await this.#repository.printLastReceipt(copies);
  }
}
